import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerModel } from 'Shared/components/design/questionnairePopup/AnswerModel';
import { QuestionPopupModel } from 'Shared/components/design/questionnairePopup/QuestionModel';
import { QuestionTitle } from 'Shared/components/design/questionnairePopup/style';
import { TextField, TextFieldTypes } from 'Shared/components/design/textField/TextField';
interface OpenEndedProps {
  question: QuestionPopupModel;
  existingAnswer?: AnswerModel;
  selectedAnswerCallback: (answer: AnswerModel | null) => void;
}

/**
 * Component for open ended questions (text/number input)
 */
function OpenEndedQuestion({question, existingAnswer, selectedAnswerCallback}: OpenEndedProps): JSX.Element {
  const [error, setError] = useState<string>('');
  const questionData      = question.data[0];
  const { t }             = useTranslation('questionnairePopup');

  /**
   * Validates the input value based on the question type
   * @param value
   * @returns true if the input is valid, false otherwise
   */
  const validateInput = (value: string): boolean => {
    if (!value) {
      setError('');
      return true;
    }

    if (questionData.answerType === 'NUMBER') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        setError(t('Questionnaire--openEndedQuestion-error-number'));
        return false;
      }
      // Default constraints for numbers if not provided
      const minValue = question.constraints?.min ?? 0;
      const maxValue = question.constraints?.max ?? 999999;

      if (numValue < minValue) {
        setError(t('Questionnaire--openEndedQuestion-error-min', { min: minValue }));
        return false;
      }
      if (numValue > maxValue) {
        setError(t('Questionnaire--openEndedQuestion-error-max', { max: maxValue }));
        return false;
      }
    } else if (questionData.answerType === 'TEXT' && question.constraints?.jsRegex) {
      const regex = new RegExp(question.constraints.jsRegex.replace(/^\/|\/[a-z]*$/g, ''));
      if (!regex.test(value)) {
        setError(t('Questionnaire--openEndedQuestion-error-text'));
        return false;
      }
    }
    setError('');
    return true;
  };

  /**
   * Handles the change event for the input field
   * @param value
   */
  const handleChange = (value: string): void => {
    const isValid = validateInput(value);
    if (isValid) {
      const answer: AnswerModel = {
        questionKey: question.key,
        value: [value]
      };
      selectedAnswerCallback(answer);
    } else if (value) {
      selectedAnswerCallback(null);
    } else {
      setError('');
      selectedAnswerCallback(null);
    }
  };

  return (
    <>
      <QuestionTitle>
        {questionData.question}
      </QuestionTitle>
      <TextField
        fieldType={error ? TextFieldTypes.ERROR : TextFieldTypes.DEFAULT}
        changeCallback={handleChange}
        value={existingAnswer?.value?.[0]?.toString() || ''}
        placeholder={questionData.answerType === 'NUMBER' ? t('Questionnaire--openEndedQuestion-placeholder-number') : t('Questionnaire--openEndedQuestion-placeholder-text')}
        helperText={error}
      />
    </>
  );
}

export default OpenEndedQuestion;